import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, Clock3, X } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import type { OperationalShiftTemplate, ShiftChecklist } from "@apartos/contracts";
import { api } from "../lib/api";
import { calendarDateKey } from "../lib/calendar";
import { externalCalendarBlocksAsBookings, hideBlocksCoveredByBookings } from "../lib/externalCalendar";
import { findOverbookings, overbookingLabel } from "../lib/overbooking";
import { useDialog } from "../hooks/useDialog";
import { emptyShiftChecklist, isShiftChecklistComplete, toShiftChecklistPayload } from "../lib/shiftChecklist";
import { ModalPortal } from "./ModalPortal";
import { ShiftChecklistFields } from "./ShiftChecklistFields";

export function ShiftAcceptModal({ options, initialTemplateId, onClose }: {
  options: OperationalShiftTemplate[];
  initialTemplateId?: string;
  onClose: () => void;
}) {
  const ref = useDialog<HTMLElement>(onClose);
  const client = useQueryClient();
  const [templateId, setTemplateId] = useState(initialTemplateId ?? (options.length === 1 ? options[0]?.id : undefined));
  const [checklist, setChecklist] = useState<ShiftChecklist>(emptyShiftChecklist);
  const mutation = useMutation({
    mutationFn: () => api.acceptOperationalShift(templateId!, toShiftChecklistPayload(checklist)),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["operational-shift"] }),
        client.invalidateQueries({ queryKey: ["dashboard"] }),
        client.invalidateQueries({ queryKey: ["settings"] }),
      ]);
      onClose();
    },
  });
  // При приёме смены администратор сразу видит овербукинг, пока его ещё можно решить.
  const bookings = useQuery({ queryKey: ["bookings"], queryFn: api.bookings });
  const blocks = useQuery({ queryKey: ["calendar-blocks"], queryFn: api.calendarBlocks });
  const overbookings = useMemo(() => {
    if (!bookings.data || !blocks.data) return [];
    const entries = [...bookings.data, ...externalCalendarBlocksAsBookings(hideBlocksCoveredByBookings(blocks.data, bookings.data))];
    return findOverbookings(entries, calendarDateKey(new Date()));
  }, [bookings.data, blocks.data]);
  const ready = Boolean(templateId) && isShiftChecklistComplete(checklist);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) mutation.mutate();
  };

  return <ModalPortal onClose={onClose}>
    <section ref={ref} className="booking-modal handover-modal" role="dialog" aria-modal="true" aria-labelledby="shift-accept-title">
      <header className="modal-header">
        <div><h2 id="shift-accept-title">Принять смену</h2><p>Смена начнётся только после того, как вы отметите все пункты</p></div>
        <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть"><X size={17}/></button>
      </header>
      <form onSubmit={submit}>
        <div className="modal-body">
          <div className="shift-accept-templates" role="radiogroup" aria-label="Рабочий интервал">
            {options.map((option) => <label key={option.id} className={templateId === option.id ? "is-selected" : ""}>
              <input type="radio" name="shift-template" checked={templateId === option.id} onChange={() => setTemplateId(option.id)}/>
              <Clock3 size={15}/><span><strong>{option.name}</strong><small>{option.start} — {option.end}</small></span>
            </label>)}
          </div>
          {overbookings.length > 0 && <div className="form-error calendar-overbooking" role="alert"><CircleAlert size={16}/><div><strong>Овербукинг — решите в начале смены</strong><ul>{overbookings.map((item) => <li key={`${item.apartmentNumber}-${item.entries.map((entry) => entry.id).join("-")}`}>{overbookingLabel(item)}</li>)}</ul><small>Закройте продажи номера в Extranet и переселите одного из гостей.</small></div></div>}
          <ShiftChecklistFields mode="accept" title="Чек-лист приёма смены" value={checklist} onChange={setChecklist}/>
          {mutation.error && <div className="form-error" role="alert"><CircleAlert size={15}/>{mutation.error.message}</div>}
        </div>
        <footer className="modal-footer">
          <button type="button" className="secondary-button" onClick={onClose}>Отмена</button>
          <button className="primary-button" disabled={!ready || mutation.isPending}>{mutation.isPending ? "Принимаем…" : "Принять смену"}</button>
        </footer>
      </form>
    </section>
  </ModalPortal>;
}
